import type { QuoteJobAddress } from 'paintscout'

/**
 * Compares a quote/invoice job address against a deal's location and returns true if they
 * meaningfully differ. Used to decide whether to warn the user (and offer to sync the two)
 * when linking a quote to a deal, or when editing either address after they've been linked.
 *
 * Returns false (no mismatch) when either side has no address at all — there's nothing to
 * compare against, so we don't want to nag. Comparison is case-insensitive and trims
 * surrounding whitespace so cosmetic differences don't count as a mismatch.
 */
export function hasAddressMismatch(
  quoteAddress?: QuoteJobAddress | null,
  dealAddress?: QuoteJobAddress | null
): boolean {
  // If the deal has no address, nothing to mismatch against
  if (!dealAddress?.address && !dealAddress?.city && !dealAddress?.province && !dealAddress?.postal) {
    return false
  }

  // If the quote has no address yet, nothing to mismatch from
  if (!quoteAddress?.address && !quoteAddress?.city && !quoteAddress?.province && !quoteAddress?.postal) {
    return false
  }

  const normalize = (val?: string | null) => (val || '').trim().toLowerCase()

  return (
    normalize(quoteAddress?.address) !== normalize(dealAddress?.address) ||
    normalize(quoteAddress?.city) !== normalize(dealAddress?.city) ||
    normalize(quoteAddress?.province) !== normalize(dealAddress?.province) ||
    normalize(quoteAddress?.postal) !== normalize(dealAddress?.postal)
  )
}
